import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { RefreshCw, Search, Download, RotateCw, Eye, Loader2 } from 'lucide-react';
import EmailStatsWidget from './EmailStatsWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyle = (s) => {
  switch (s) {
    case 'sent':
    case 'delivered': return 'bg-emerald-500';
    case 'opened': return 'bg-blue-500';
    case 'clicked': return 'bg-indigo-500';
    case 'queued': return 'bg-amber-500';
    case 'failed': return 'bg-rose-500';
    case 'skipped': return 'bg-slate-400';
    default: return 'bg-slate-400';
  }
};

const statusLabel = (s) => ({
  sent: 'Verstuurd', delivered: 'Afgeleverd', opened: 'Geopend', clicked: 'Geklikt',
  queued: 'In wachtrij', failed: 'Mislukt', skipped: 'Overgeslagen',
}[s] || s || '—');

const triggerLabel = (t) => ({
  aanmelding_ontvangen: 'Aanmelding ontvangen',
  in_behandeling: 'In behandeling',
  ingediend: 'Ingediend',
  beslissing: 'Beslissing',
  gewonnen: 'Gewonnen',
  afgewezen: 'Afgewezen',
  __test__: 'Testmail',
}[t] || t || '—');

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [trigger, setTrigger] = useState('all');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [resending, setResending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (status !== 'all') params.set('status', status);
      if (trigger !== 'all') params.set('trigger', trigger);
      const res = await axios.get(`${API}/email/logs?${params.toString()}`);
      setLogs(res.data);
    } catch (e) { toast.error('Laden mislukt'); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, trigger]);

  const resend = async (log) => {
    setResending(log.id);
    try {
      await axios.post(`${API}/email/logs/${log.id}/resend`);
      toast.success('E-mail opnieuw verstuurd');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opnieuw versturen mislukt');
    } finally { setResending(null); }
  };

  return (
    <div className="space-y-4" data-testid="email-logs-tab">
      <EmailStatsWidget />
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Zoek op naam, e-mail, zaaknummer of onderwerp…" className="pl-9" data-testid="log-search" />
          </div>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="log-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="sent">Verstuurd</SelectItem>
            <SelectItem value="queued">In wachtrij</SelectItem>
            <SelectItem value="failed">Mislukt</SelectItem>
            <SelectItem value="skipped">Overgeslagen</SelectItem>
          </SelectContent>
        </Select>
        <Select value={trigger} onValueChange={setTrigger}>
          <SelectTrigger className="w-52" data-testid="log-trigger-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle triggers</SelectItem>
            <SelectItem value="aanmelding_ontvangen">Aanmelding ontvangen</SelectItem>
            <SelectItem value="in_behandeling">In behandeling</SelectItem>
            <SelectItem value="ingediend">Ingediend</SelectItem>
            <SelectItem value="beslissing">Beslissing</SelectItem>
            <SelectItem value="gewonnen">Gewonnen</SelectItem>
            <SelectItem value="afgewezen">Afgewezen</SelectItem>
            <SelectItem value="__test__">Testmail</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="log-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Vernieuwen</Button>
        <a href={`${API}/email/logs/export.csv`} download>
          <Button variant="outline" data-testid="log-export"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
        </a>
      </div>

      <Card className="border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Ontvanger</th>
                <th className="px-4 py-3">Zaak</th>
                <th className="px-4 py-3">Trigger</th>
                <th className="px-4 py-3">Onderwerp</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actie</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">Nog geen e-mails verzonden.</td></tr>
              )}
              {!loading && logs.map(l => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`log-row-${l.id}`}>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmtDate(l.sent_at || l.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{l.recipient_name || '—'}</div>
                    <div className="text-xs text-slate-500">{l.recipient_email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{l.kenmerk || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{triggerLabel(l.trigger)}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-xs truncate" title={l.subject}>{l.subject || '—'}</td>
                  <td className="px-4 py-3">
                    <Badge className={statusStyle(l.status)}>{statusLabel(l.status)}</Badge>
                    {l.error && <div className="text-xs text-rose-600 mt-1 max-w-xs truncate" title={l.error}>{l.error}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(l)} data-testid={`view-log-${l.id}`}><Eye className="w-4 h-4" /></Button>
                    {(l.status === 'failed' || l.status === 'sent') && (
                      <Button size="sm" variant="ghost" onClick={() => resend(l)} disabled={resending === l.id} data-testid={`resend-log-${l.id}`}>
                        {resending === l.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>E-mail details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500 uppercase">Datum</div><div>{fmtDate(detail.sent_at || detail.created_at)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Status</div><Badge className={statusStyle(detail.status)}>{statusLabel(detail.status)}</Badge></div>
                <div><div className="text-xs text-slate-500 uppercase">Ontvanger</div><div>{detail.recipient_name} &lt;{detail.recipient_email}&gt;</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Zaak</div><div className="font-mono text-xs">{detail.kenmerk || '—'}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Trigger</div><div>{triggerLabel(detail.trigger)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Resend ID</div><div className="font-mono text-xs break-all">{detail.resend_id || '—'}</div></div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Onderwerp</div>
                <div className="font-semibold text-[#1e1b4b]">{detail.subject}</div>
              </div>
              {detail.error && (
                <div className="border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-800"><strong>Fout:</strong> {detail.error}</div>
              )}
              <div>
                <div className="text-xs text-slate-500 uppercase mb-1">HTML</div>
                <div className="border border-slate-200 rounded-lg p-4 bg-white prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: detail.html_body || '' }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailLogs;
